import { useState } from 'react';
import { Images, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useGetTenantImageLibraryQuery } from '@/api/tenants/tenantImageLibrary';

interface ImageGalleryPickerButtonProps {
  tenantCode: string;
  onSelect: (url: string) => void;
  disabled?: boolean;
}

function getDisplayFilename(name: string): string {
  const parts = name.split('/');
  return parts[parts.length - 1] ?? name;
}

export function ImageGalleryPickerButton({
  tenantCode,
  onSelect,
  disabled,
}: ImageGalleryPickerButtonProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useGetTenantImageLibraryQuery(open ? tenantCode : undefined);

  const images = data?.images ?? [];
  const filteredImages = search.trim()
    ? images.filter(image =>
        getDisplayFilename(image.name).toLowerCase().includes(search.toLowerCase())
      )
    : images;

  const handleSelect = (url: string) => {
    onSelect(url);
    setOpen(false);
    setSearch('');
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            <Images className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Select from gallery</TooltipContent>
      </Tooltip>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Select from Image Gallery</DialogTitle>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by filename..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredImages.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                No images found
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {filteredImages.map(image => (
                  <button
                    key={image.url}
                    type="button"
                    onClick={() => handleSelect(image.url)}
                    className="group overflow-hidden rounded-md border bg-muted/30 text-left transition-colors hover:border-primary hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="aspect-video w-full overflow-hidden bg-muted">
                      <img
                        src={image.url}
                        alt={getDisplayFilename(image.name)}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                    <p className="truncate px-2 py-1.5 text-xs text-muted-foreground">
                      {getDisplayFilename(image.name)}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
